import { entityProxyModeSignal } from '../state/signals/userSettingsSignals'
import { entityProxy } from '../utils/debug'
import { defaultTransformer } from '../utils/utils'

const entityProxyHelper = (entity, debugInfo = null) => {
    let print = false
    if (!entityProxyModeSignal.value) {
        if (print) {
            console.log("Entity proxy mode is disabled")
        }
        return entity
    }
    else if (print) {
        console.log("Entity proxy mode is enabled")
    }
    const type = typeof entity
    if (type == 'undefined' || entity == null) {
        if (print) {
            console.log("API call returned null, or does not have a return value")
        }
        return entity
    }
    else if (type != 'object') {
        if (print) {
            console.log("API call did not return an object")
        }
        return type
    }
    else if (entity instanceof Response) {
        console.log(`Response object returned by API call to "${debugInfo}"`)
        return entity
    }
    else if (entity instanceof ArrayBuffer) {
        if (print) {
            console.log("transformer returned an array buffer, skipping entity proxy")
        }
        return entity
    }
    else if (print) {
        console.log(`Fetched the following from URL "${debugInfo}"`)
        console.log(entity)
    }
    if (Array.isArray(entity)) {
        if (print) {
            console.log(`The API call to "${ debugInfo }" returned an array`)
        }
        let arr = []
        for (let i = 0; i < entity.length; i++) {
            const proxy = entityProxyHelper(entity[i], debugInfo)
            arr.push(proxy)
        }
        if (print) {
            console.log("Returning the following proxies:")
            console.log(arr)
        }
        return arr
    }
    else if (print) {
        console.log("The API call returned a singluar entity")
    }
    const proxy = entityProxy(entity)
    if (debugInfo) {
        proxy.debugInfo = debugInfo
    }
    if (print) {
        console.log("Generated the following proxy:")
        console.log(proxy)
    }
    return proxy
}

/**
 * 
 * @param {string} path: Request URI
 * @param {string} accessToken: inserted into Authorizaztion header field if present
 * @param {object} options: Request body
 * @param {function} transformer: function for processing return value of a successful API call
 * @param {signal} controllerSignal: AbortController signal to be passed into the request body
 * @param {bool} envelope: if true, headers for the shift4 request envelope will be automatically applied
 * @param {bool} proxy: if true, entities will be wrapped with proxies rather
 * @returns
 */
export async function apiClient(
    path,
    accessToken = null,
    options = {},
    transformer = defaultTransformer,
    controllerSignal = null,
    envelope = true,
    proxy = true
) {
    let print = false
    if (options == null) {
        options = {}
    }
    let request = { ...options }
    if (!("headers" in options)) {
        request.headers = {}
    }
    else {
        request.headers = {
            ...options.headers
        }
    }
    if (controllerSignal && !("signal" in options)) {
        if (print) {
            console.log("Setting abort controller signal in options")
        }
        request.signal = controllerSignal
    }
    else if (print) {
        console.log("Abort controller signal does not have a value")
    }
    if (accessToken) {
        if (!('Authorization' in request.headers)) {
            request.headers.Authorization = `Token token="${accessToken}"`
        }
    }
    if (envelope) {
        if (!('Shift4-Rest-Request-Envelope-Version' in request.headers)) {
            request.headers['Shift4-Rest-Request-Envelope-Version'] = '1'
        }
        if (!('Shift4-Rest-Response-Envelope-Version' in request.headers)) {
            request.headers['Shift4-Rest-Response-Envelope-Version'] = '1'
        }
    }
    if (Object.keys(request.headers).length > 0 && !('Content-Type' in request.headers)) {
        request.headers['Content-Type'] = 'application/json'
    }
    const response = await fetch(path, {
        ...request
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
            throw error
        }
        else {
            let body = null
            try {
                body = await response.json()
            } catch {
                // response body is not JSON
            }
            let message
            if (body && body.response && Array.isArray(body.response.messages) && body.response.messages.length > 0) {
                message = body.response.messages.join(' ')
            } else if (response.statusText) {
                message = `API request failed. Status: ${response.statusText}`
            } else {
                message = `API request failed with status code ${response.status}`
            }
            error = new Error(message)
            error.code = response.status
            throw error
        }
    }
    if (transformer && typeof transformer == 'function') {
        const entity = await transformer(response)
        if (proxy) {
            return entityProxyHelper(entity, path)
        }
        return entity
    }
    else if (print) {
        console.log("No transformer function, returning the response")
    }
    return response
}